import { accountLabel, type ChartOfAccount } from '@clockwise/clients/chart-of-accounts';
import { useId, useMemo } from 'react';
import { CheckRow, Combobox } from '@/components/custom/combobox';
import { useChartOfAccounts } from '@/lib/chart-of-accounts';

interface Props {
  /** Selected account id, or null for "no account". */
  value: string | null;
  onChange: (accountId: string | null) => void;
  disabled?: boolean;
  hasError?: boolean;
  placeholder?: string;
  className?: string;
  id?: string;
  /** Accessible name when no visible label is associated. */
  'aria-label'?: string;
  /** Accounts that may not be chosen here — e.g. ones already used by other
   *  rows of the same split. The current value is never excluded. */
  exclude?: ReadonlySet<string>;
}

const matches = (a: ChartOfAccount, q: string) =>
  `${a.code} ${a.name}`.toLowerCase().includes(q.trim().toLowerCase());

/**
 * Pick one account from the read-only chart of accounts. Lists active
 * accounts, searchable by code or name. An inactive account still shows when
 * it is the current value — a record may keep what it already pointed at —
 * but cannot be newly chosen.
 */
export function AccountPicker({
  value,
  onChange,
  disabled,
  hasError,
  placeholder = 'Select account',
  className,
  id,
  exclude,
  'aria-label': ariaLabel,
}: Props) {
  const { accounts, byId, isLoading } = useChartOfAccounts();
  const autoId = useId();
  const inputId = id ?? autoId;
  const items = useMemo(
    () => accounts.filter((a) => a.id === value || (a.active && !exclude?.has(a.id))),
    [accounts, value, exclude],
  );
  const selected = value ? (byId.get(value) ?? null) : null;

  return (
    <div className={className}>
      {/* A picker in a table cell has no visible label; this names its input. */}
      {ariaLabel ? (
        <label htmlFor={inputId} className="sr-only">
          {ariaLabel}
        </label>
      ) : null}
      <Combobox<ChartOfAccount>
        id={inputId}
        items={items}
        selectedItem={selected}
        value={value}
        onChange={(a) => onChange(a?.id ?? null)}
        getId={(a) => a.id}
        getLabel={accountLabel}
        filterFn={matches}
        loading={isLoading}
        placeholder={placeholder}
        disabled={disabled}
        hasError={hasError}
        emptyText="No account matches."
        popoverWidth="auto"
        renderItem={(a, { isSelected }) => (
          <CheckRow
            isSelected={isSelected}
            label={a.name === a.code ? a.code : a.name}
            secondary={a.name === a.code ? undefined : a.code}
          />
        )}
      />
    </div>
  );
}
